import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import get from 'lodash/get';
import { useRouter } from 'next/router';

import Link from '@g4/next-furniture/Link';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';
import { TrackClick } from '@g4/trackster';
import tracking from '@g4/g4-analytics';

import { useMedia, Box, Text, VerticalSpacing } from '../../Boneyard';
import { useAuthentication } from '../../../graphql';
import NavGroup from './NavGroup';
import ExpandableMenuItem from './navGroup/ExpandableMenuItem';
import Markdown from '../../../components/Markdown';
import {
  HEADER_USER_MENU,
  BOOKING_LOGIN,
} from '../../../../automationHooks.json';
import { useModal } from '../../ModalProvider';
import {
  withTranslations,
  useTranslations,
} from '../../../graphql/components/TranslationsProvider';
import { isActiveLoyaltyUser } from '../../../utils/loyaltyHelpers';

import USERMENU_TRANSLATIONS from './userMenu/userMenuTranslations.query.graphql.js';
import LoginSignup, {
  LoginSignupTitle,
  LoginSignupHeaderLogo,
} from '../../LoginSignup';
import useUserMenuQuery from './useUserMenuQuery';
import { LOGOUT_PATH } from '../../../utils/authUtils';
import DEFAULT_AVATAR from '../../../images/ic-default-avatar.png';
import PrintAware from '../../PrintAware';
import { useSSRQuery } from '../../../graphql';
import TRAVEL_AGENT_LOGIN_QUERY from '../siteHeader/siteHeaderTranslations.query.graphql.js';
import { hasTargetingConsent } from '../../../utils/ga4Utils';

const UserMenu = ({
  theme,
  isBurgerMenu,
  pageContext,
  isLoyaltyPaymentUsed,
}) => {
  const {
    isAuthenticated,
    logout,
    iataNumber,
    agentName,
  } = useAuthentication();

  const mq = useMedia();
  const translations = useTranslations();
  const { isTravelAgent } = useUserContext();
  const router = useRouter();

  const { data } = useSSRQuery(TRAVEL_AGENT_LOGIN_QUERY, {
    skip: !isTravelAgent,
  });
  const travelAgentLoginPage =
    data?.application?.translations?.travelAgentLoginPage;

  const {
    setShowModal,
    setLogo,
    setTitle,
    setIsMobile,
    setContent,
    setAutomationHook,
  } = useModal();

  const openModal = e => {
    tracking.trackEvent('site_click_tracking', 'Top Nav', 'Login', null);
    e.preventDefault();
    setTitle(<LoginSignupTitle />);
    setLogo(<LoginSignupHeaderLogo isMobile={mq.lte('sm')} />);
    setContent(<LoginSignup isMobile={mq.lte('sm')} />);
    setIsMobile(mq.lte('sm'));
    setShowModal(true);
    setAutomationHook(BOOKING_LOGIN.hook);
  };

  const userMenuQueryVariables =
    pageContext === 'Confirmation' && isLoyaltyPaymentUsed
      ? { refetch: true }
      : { refetch: false };

  const {
    loading,
    error,
    refetchUserMenu,
    id,
    firstName,
    lastName,
    loyalty,
    userMenuItems,
  } = useUserMenuQuery({
    skipSummary: !isAuthenticated,
    variables: userMenuQueryVariables,
  });

  const refetchUserMenuData = useCallback(() => {
    refetchUserMenu && refetchUserMenu();
  }, [refetchUserMenu]);

  useEffect(() => {
    refetchUserMenuData();
    // Always clear allways_account_no when not authenticated, including on mount
    if (!isAuthenticated) {
      tracking.setDataLayerVars({ allways_account_no: '' });
    }
  }, [isAuthenticated, refetchUserMenuData]);

  // Returns login button for unauthenticated user
  if (!isAuthenticated && !isTravelAgent) {
    return (
      <NavGroup
        items={[
          {
            __typename: 'NavigationMenuItem',
            id: '7411',
            icon: 'ALLWAYS',
            title: 'Log in',
            style: {
              fontColor: '#ffffff',
            },
            link: null,
            onclick: openModal,
          },
        ]}
        automationHook={HEADER_USER_MENU.hook}
        displayAsColumn={mq.lt('md')}
      />
    );
  }

  if (loading) {
    return null;
  }

  if (error) {
    throw error;
  }

  const Menu = styled(Box)`
    display: flex;
    width: ${mq.lte('sm') ? '100%' : '320px'};
    flex-direction: column;
    background-color: #ffffff;
    border: 1px inside #cfd8dc;
    padding: ${mq.lte('sm') ? '0px' : '15px 20px'};
    border-radius: 0 0 8px 8px;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.4);
    color: ${theme.colors.common.peacockBlue};
  `;

  const MenuHeader = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: center;
    border: 1px inside #cfd8dc;
    position: relative;
    top: 5px;
  `;

  const Separator = styled.div`
    width: '100%';
    height: 2px;
    background-color: #f89728;
  `;

  const Avatar = styled.img.attrs({
    src: DEFAULT_AVATAR,
    alt: 'Avatar',
  })`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    z-index: 1;
  `;

  const LinkText = styled.div`
    color: ${theme.colors.siteHeader.light.linkColor};
    padding: 3px;
    font-size: ${theme.toRem(16)};
    font-weight: 600;
    border-bottom: 5px solid transparent;
    transition: border-bottom 0.2s ease-in-out;

    &:hover {
      border-bottom: 5px solid ${theme.colors.siteHeader.linkActive};
    }
  `;

  const isActiveLoyaltyMember = isActiveLoyaltyUser(loyalty?.status);

  const isLogoutLink = link => link && link.path === LOGOUT_PATH;

  // Renders a nav item with icon and styles
  const NavItem = ({ item }) => {
    const { link, title } = item;
    const href = link.path || link.url;
    const linkTarget = `_${link.target.toLowerCase()}`;
    const logoutCallback = isLogoutLink(link) && {
      onClick: e => {
        if (window.location.search === `?password_updated=true`) {
          router.push(`${window.location.pathname}`);
        }
        e.preventDefault();
        window.localStorage.removeItem('userDetails');
        logout && logout();
      },
    };

    return (
      <Link href={href} target={linkTarget}>
        {({ anchorProps }) => (
          <TrackClick
            category="site_navigation_clicks"
            action={href}
            label={title}
          >
            <Box
              as="a"
              d={'flex'}
              direction={'row'}
              alignItems="center"
              {...anchorProps}
              {...logoutCallback}
            >
              <LinkText>
                <Text size={isLogoutLink(link) ? 14 : 16}>{title}</Text>
              </LinkText>
            </Box>
          </TrackClick>
        )}
      </Link>
    );
  };

  const renderPoints = loyalty => {
    if (!loyalty) {
      return null;
    }

    const { total, pointsValue } = loyalty;

    tracking.setDataLayerVars({
      loyalty_points_total: isAuthenticated ? total : 0,
      allways_account_no: isAuthenticated && hasTargetingConsent() ? id : '',
    });

    return (
      <Text weight={600} size={16}>
        <Markdown
          source={`{{totalPoints}} Points = {{pointsValue}}`}
          data={{
            totalPoints: (total > 0 ? total : 0).toLocaleString(),
            pointsValue: `$${(pointsValue > 0 ? pointsValue : 0).toFixed(2)}`,
          }}
        />
      </Text>
    );
  };

  const getAuthenticatedExpandableMenuItem = (
    loyalty,
    firstName,
    agentName
  ) => {
    const displayLoggedUser = isTravelAgent ? agentName : firstName;

    const headerGreeting = get(translations, 'usermenu.headerGreeting').replace(
      '{{firstName}}',
      displayLoggedUser
    );

    let description, icon;

    if (isTravelAgent) {
      description = {
        icon: 'ALLWAYS',
        details: travelAgentLoginPage.taIataNumberDescription,
      };
    }

    if (loyalty && isActiveLoyaltyMember) {
      const { pointsValue } = loyalty;

      const headerPointsBalance = get(
        translations,
        'usermenu.headerPointsBalanceText'
      )
        .replace(
          '{{pointsValue}}',
          `<b>${(pointsValue > 0 ? pointsValue : 0).toFixed(2)}</b>`
        )
        .replace('#015699', 'white');

      //^^ TODO: Put placeholder for points value color in schema instead of #015699

      description = {
        icon: 'ALLWAYS',
        details: headerPointsBalance,
      };
    } else {
      icon = 'ALLWAYS';
    }

    return {
      __typename: 'ExpandableMenuItem',
      id: 'my-allegiant',
      title: headerGreeting,
      trackLabel: 'Login',
      style: {
        fontSize: '14px',
        fontColor: '#ffffff',
      },
      link: null,
      expanded: mq.lt('md'),
      items: {},
      description,
      icon,
    };
  };

  const redirectToTALogin = async () => {
    //Log out will redirect to travel agent login page
    tracking.setDataLayerVars({ allways_account_no: '' });
    logout && (await logout());
    window.location.href = '/ta-login';
  };

  const menuItems = [...userMenuItems];

  const headerItem = menuItems.shift();
  const footerItem = menuItems.pop();

  if (isBurgerMenu) {
    return (
      <Box>
        <Avatar />
        <VerticalSpacing size={2} />
        <Box d={'flex'} direction={'column'}>
          <Text weight={500} size={16} color="#263238">
            {`${firstName} ${lastName.charAt(0).toUpperCase()}.`}
          </Text>
          <VerticalSpacing size={1} />
          {isActiveLoyaltyMember && renderPoints(loyalty)}
          <Text weight={500} size={12}>
            <Markdown
              source={`Allways™ #{{customerId}}`}
              data={{
                customerId: id,
              }}
            />
          </Text>
        </Box>
        <VerticalSpacing size={4} />
        <NavItem item={headerItem} />
        <VerticalSpacing size={4} />
        <Separator />
        <VerticalSpacing size={4} />
        <Box d={'flex'} direction={'column'}>
          {menuItems.map(item => (
            <div key={item.id}>
              <NavItem item={item} />
              <VerticalSpacing size={1} />
            </div>
          ))}
        </Box>
        <VerticalSpacing size={2} />
        <Separator />
        <VerticalSpacing size={4} />
        <NavItem item={footerItem} />
        <VerticalSpacing size={2} />
      </Box>
    );
  }

  return (
    <PrintAware>
      <ExpandableMenuItem
        item={getAuthenticatedExpandableMenuItem(loyalty, firstName, agentName)}
        isCustomMenu={true}
        isMultiLineItem={isActiveLoyaltyMember}
        alignRight={true}
      >
        <Menu>
          <MenuHeader>
            <Avatar />
            {!isTravelAgent && (
              <Box d={'flex'} direction={'column'} p={2}>
                <Text weight={500} size={16} color="#263238">
                  {`${firstName} ${lastName.charAt(0).toUpperCase()}.`}
                </Text>
                <VerticalSpacing size={1} />
                {isActiveLoyaltyMember && renderPoints(loyalty)}
                <Text weight={500} size={12}>
                  <Markdown
                    source={`Allways™ #{{customerId}}`}
                    data={{
                      customerId: id,
                    }}
                  />
                </Text>
              </Box>
            )}
            {isTravelAgent && (
              <Box d={'flex'} direction={'column'} p={2}>
                <Text weight={500} size={16}>
                  {`${agentName}.`}
                </Text>
                <VerticalSpacing size={1} />
                <Text weight={300} size={12}>
                  <Markdown
                    source={travelAgentLoginPage.taIataNumberLabel}
                    data={{ iataNumber: iataNumber }}
                  />
                </Text>
              </Box>
            )}
          </MenuHeader>
          <VerticalSpacing size={4} />
          {!isTravelAgent ? (
            <>
              <NavItem item={headerItem} />
              <VerticalSpacing size={4} />
              <Separator />
              <VerticalSpacing size={4} />
              <Box d={'flex'} direction={'column'}>
                {menuItems.map(item => (
                  <div key={item.id}>
                    <NavItem item={item} />
                    <VerticalSpacing size={1} />
                  </div>
                ))}
              </Box>
              <VerticalSpacing size={2} />
              <Separator />
              <VerticalSpacing size={4} />
              <NavItem item={footerItem} />
              <VerticalSpacing size={2} />
            </>
          ) : (
            <>
              <VerticalSpacing size={4} />
              <Separator />
              <VerticalSpacing size={4} />
              <Box
                as="a"
                d={'flex'}
                direction={'row'}
                alignItems="center"
                onClick={redirectToTALogin}
              >
                <LinkText>{travelAgentLoginPage.taLogOutLabel}</LinkText>
              </Box>
            </>
          )}
        </Menu>
      </ExpandableMenuItem>
    </PrintAware>
  );
};

UserMenu.propTypes = {
  item: PropTypes.object,
  theme: PropTypes.object.isRequired,
  isBurgerMenu: PropTypes.bool,
  pageContext: PropTypes.string,
  isLoyaltyPaymentUsed: PropTypes.bool,
};

export default withTheme(withTranslations(USERMENU_TRANSLATIONS, UserMenu));
